
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {AccountRestoreResponse} from './AccountRestoreResponse';

/**
 * AccountRestoreRequest struct
 */
class AccountRestoreRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!number=} login
   * @param {!UUID=} resolvedGroupId
   * @param {!string=} resolvedGroupName
   * @param {!number=} restoredTimestamp
   * @constructor
   */
  constructor (parent = new Request(), login = new UInt64(0, 0), resolvedGroupId = new UUID(), resolvedGroupName = '', restoredTimestamp = new UInt64(0, 0)) {
    super()
    super.copy(parent)
    this.Login = login
    this.ResolvedGroupId = resolvedGroupId
    this.ResolvedGroupName = resolvedGroupName
    this.RestoredTimestamp = restoredTimestamp
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AccountRestoreRequest}
   * @param {!AccountRestoreRequest} other Other struct
   * @returns {!AccountRestoreRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Login != null) {
      this.Login = UInt64.fromNumber(other.Login)
    } else {
      this.Login = null
    }
    if (other.ResolvedGroupId != null) {
      this.ResolvedGroupId = new UUID(other.ResolvedGroupId)
    } else {
      this.ResolvedGroupId = null
    }
    if (other.ResolvedGroupName != null) {
      this.ResolvedGroupName = other.ResolvedGroupName
    } else {
      this.ResolvedGroupName = null
    }
    if (other.RestoredTimestamp != null) {
      this.RestoredTimestamp = UInt64.fromNumber(other.RestoredTimestamp)
    } else {
      this.RestoredTimestamp = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AccountRestoreRequest}
   * @returns {!AccountRestoreRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AccountRestoreRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AccountRestoreRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AccountRestoreRequest}
   * @param {!AccountRestoreRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AccountRestoreRequest)) {
      throw new TypeError('Instance of AccountRestoreRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AccountRestoreRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Login: ((this.Login != null) ? this.Login.toNumber() : null),
      ResolvedGroupId: ((this.ResolvedGroupId != null) ? this.ResolvedGroupId.toString() : null),
      ResolvedGroupName: ((this.ResolvedGroupName != null) ? this.ResolvedGroupName : null),
      RestoredTimestamp: ((this.RestoredTimestamp != null) ? this.RestoredTimestamp.toNumber() : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AccountRestoreRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AccountRestoreRequest} other Object value
   * @returns {!AccountRestoreRequest} Created struct
   */
  static fromObject (other) {
    return new AccountRestoreRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AccountRestoreRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AccountRestoreRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AccountRestoreRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4205
  }
}

export { AccountRestoreRequest };

/**
 * Fast Binary Encoding AccountRestoreRequest field model
 */
class FieldModelAccountRestoreRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.Login = new fbe.FieldModelUInt64(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.ResolvedGroupId = new fbe.FieldModelUUID(buffer, this.Login.fbeOffset + this.Login.fbeSize)
    this.ResolvedGroupName = new fbe.FieldModelString(buffer, this.ResolvedGroupId.fbeOffset + this.ResolvedGroupId.fbeSize)
    this.RestoredTimestamp = new fbe.FieldModelUInt64(buffer, this.ResolvedGroupName.fbeOffset + this.ResolvedGroupName.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelAccountRestoreRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelAccountRestoreRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAccountRestoreRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Login.fbeSize + this.ResolvedGroupId.fbeSize + this.ResolvedGroupName.fbeSize + this.RestoredTimestamp.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAccountRestoreRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Login.fbeExtra + this.ResolvedGroupId.fbeExtra + this.ResolvedGroupName.fbeExtra + this.RestoredTimestamp.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAccountRestoreRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAccountRestoreRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAccountRestoreRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4205
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAccountRestoreRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAccountRestoreRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.ResolvedGroupId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ResolvedGroupId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ResolvedGroupId.fbeSize

    if ((fbeCurrentSize + this.ResolvedGroupName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ResolvedGroupName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ResolvedGroupName.fbeSize

    if ((fbeCurrentSize + this.RestoredTimestamp.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RestoredTimestamp.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RestoredTimestamp.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAccountRestoreRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAccountRestoreRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAccountRestoreRequest}
   * @param {!AccountRestoreRequest} fbeValue Default value, defaults is new AccountRestoreRequest()
   * @returns {!AccountRestoreRequest} AccountRestoreRequest value
   */
  get (fbeValue = new AccountRestoreRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAccountRestoreRequest}
   * @param {!AccountRestoreRequest} fbeValue AccountRestoreRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Login.fbeSize) <= fbeStructSize) {
      fbeValue.Login = this.Login.get()
    } else {
      fbeValue.Login = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.ResolvedGroupId.fbeSize) <= fbeStructSize) {
      fbeValue.ResolvedGroupId = this.ResolvedGroupId.get()
    } else {
      fbeValue.ResolvedGroupId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ResolvedGroupId.fbeSize

    if ((fbeCurrentSize + this.ResolvedGroupName.fbeSize) <= fbeStructSize) {
      fbeValue.ResolvedGroupName = this.ResolvedGroupName.get()
    } else {
      fbeValue.ResolvedGroupName = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ResolvedGroupName.fbeSize

    if ((fbeCurrentSize + this.RestoredTimestamp.fbeSize) <= fbeStructSize) {
      fbeValue.RestoredTimestamp = this.RestoredTimestamp.get()
    } else {
      fbeValue.RestoredTimestamp = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RestoredTimestamp.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAccountRestoreRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAccountRestoreRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAccountRestoreRequest}
   * @param {!AccountRestoreRequest} fbeValue AccountRestoreRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAccountRestoreRequest}
   * @param {AccountRestoreRequest} fbeValue AccountRestoreRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Login.set(fbeValue.Login)
    this.ResolvedGroupId.set(fbeValue.ResolvedGroupId)
    this.ResolvedGroupName.set(fbeValue.ResolvedGroupName)
    this.RestoredTimestamp.set(fbeValue.RestoredTimestamp)
  }
}

export { FieldModelAccountRestoreRequest };

/**
 * Fast Binary Encoding AccountRestoreRequest model
 */
class AccountRestoreRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAccountRestoreRequest(this.buffer, 4)
  }

  /**
   * Get the AccountRestoreRequest model
   * @this {!AccountRestoreRequestModel}
   * @returns {!FieldModelAccountRestoreRequest} model AccountRestoreRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AccountRestoreRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AccountRestoreRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AccountRestoreRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AccountRestoreRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAccountRestoreRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AccountRestoreRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AccountRestoreRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AccountRestoreRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AccountRestoreRequestModel}
   * @param {!AccountRestoreRequest} value AccountRestoreRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AccountRestoreRequestModel}
   * @param {!AccountRestoreRequest} value AccountRestoreRequest value, defaults is new AccountRestoreRequest()
   * @return {!object} Deserialized AccountRestoreRequest value and its size
   */
  deserialize (value = new AccountRestoreRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AccountRestoreRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AccountRestoreRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AccountRestoreRequestModel}
   * @param {!number} prev Previous AccountRestoreRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AccountRestoreRequestModel };
AccountRestoreRequest.__has_response = true;
AccountRestoreRequest.__response_class = AccountRestoreResponse;
